<script setup lang="ts">
import {
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuPortal,
	DropdownMenuRoot,
	DropdownMenuTrigger,
} from "reka-ui";

/**
 * Design-system primitive: tokens only, no business knowledge, no network.
 *
 * Um menu de COMANDOS: uma lista curta em que cada linha faz uma coisa.
 *
 * ── POR QUE OS ITENS SÃO UMA PROP E NÃO UM SLOT ──────────────────────────
 *
 * Porque é isso que separa este arquivo do `Popover.vue` ao lado. Um menu tem
 * setas, Home/End, digitação para saltar e `role="menuitem"` em cada linha — e
 * nada disso existe se as linhas forem markup arbitrário de quem chama. Declarar
 * os itens é o que permite entregar o teclado de graça. Painel com conteúdo
 * livre (uma busca, uma grade, um formulário) é `Popover`, e o preço de escolher
 * errado é só o teclado.
 */
type MenuItem = {
	value: string;
	label: string;
	/** Uma segunda linha, para o que o rótulo não cabe dizer. */
	hint?: string;
	tone?: "default" | "danger";
	disabled?: boolean;
};

withDefaults(
	defineProps<{
		items: readonly MenuItem[];
		/** O painel sai do fluxo: sem nome, um leitor de tela anuncia "grupo". */
		label: string;
		side?: "top" | "bottom" | "left" | "right";
		align?: "start" | "center" | "end";
		panelClass?: string;
	}>(),
	{ side: "bottom", align: "start", panelClass: "w-56" },
);

const emit = defineEmits<{ select: [value: string] }>();
</script>

<template>
	<DropdownMenuRoot>
		<DropdownMenuTrigger as-child>
			<slot name="trigger" />
		</DropdownMenuTrigger>

		<DropdownMenuPortal>
			<DropdownMenuContent
				class="z-50 overflow-hidden rounded-xl border border-border bg-surface-raised p-1 shadow-md focus:outline-none"
				:class="panelClass"
				:side="side"
				:align="align"
				:side-offset="6"
				:collision-padding="8"
				:aria-label="label"
				avoid-collisions
			>
				<DropdownMenuItem
					v-for="item in items"
					:key="item.value"
					class="cursor-default rounded-lg px-3 py-1.5 text-xs data-[disabled]:opacity-50 data-[highlighted]:bg-bg-muted focus:outline-none"
					:class="item.tone === 'danger' ? 'text-danger' : 'text-fg'"
					:disabled="item.disabled"
					@select="emit('select', item.value)"
				>
					{{ item.label }}
					<span v-if="item.hint" class="mt-0.5 block text-3xs text-fg-subtle">{{ item.hint }}</span>
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenuPortal>
	</DropdownMenuRoot>
</template>
